import { OrderDirection } from "_shared/_api/ordering/order-direction";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import EntityMember from "_shared/entity-members/entity-member";
import Guid from "_shared/guids/guid";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import UserCoworkerQuery, {
  UserCoworkerFilterParameters,
  UserCoworkerOrderField,
  UserCoworkerOrderParameters,
} from "users/information/coworker-information/_api/commands/query-user-coworker-api-request";
import { useLazyGetUserCoworkersQuery } from "users/information/coworker-information/_api/user-coworker-api";
import React from "react";
import {
  addAllColleaguesPrompt,
  colleaguesLeftOutForCredentials,
  colleaguesToAdd,
  fetchAllColleagues,
  nobodyAddedForCredentialsMessage,
} from "work/work-agreements/teams/vendor-team/_views/members/add-all-colleagues";

/** What the roster's add control calls this way of filling a team. */
export const ADD_ALL_COLLEAGUES_LABEL = "Add All Colleagues";

export type AddAllColleaguesParams = {
  /** The user ids already on this roster — its leader and every member — who must not be added twice. */
  readonly alreadyOnTeamUserIds: readonly Guid[];
  /**
   * Whether this roster's team is restricted, which changes what the press costs rather than
   * whether it is allowed. See `addAllColleaguesPrompt`.
   */
  readonly teamRestricted?: boolean;
  /**
   * Called once with everyone to add, never once per person: both rosters accumulate additions from
   * a value captured at render, so a loop of single adds would keep only the last one.
   */
  readonly onColleaguesChosen: (members: EntityMember[]) => void;
};

export type AddAllColleaguesAction = {
  /** Fills the team with the firm's roster, after asking. */
  addAllColleagues: () => void;
  /**
   * True while the colleagues are being fetched. The control it is handed to is offered greyed out
   * rather than withheld: a firm of forty is two round trips, and a second press in the meantime would
   * ask a second time and add everyone twice.
   */
  isWorking: boolean;
};

/**
 * "Add all colleagues", for a firm that wants its whole roster on the team.
 *
 * A HOOK rather than a button of its own. It used to be an icon-only button parked at the right of the
 * roster's heading, which is one of the two controls #4955's review asked to be taken off the team
 * tables; the capability is not lost with it, because a roster's add control is a split button now and
 * this is one of the ways to add it offers (see `table-add-actions`). The caller decides whether to
 * offer it at all — `mayAddAllColleagues` answers whose roster it is — because a proposal's vendor team
 * is editable by the counterparty too, and a client's colleagues have no business on the firm's team.
 *
 * The confirmation carries the count, which is why the colleagues are fetched before it is asked
 * rather than after. A press that turns out to add nobody says so and asks nothing. Colleagues whose
 * profiles list no professional credentials are left out (#5800), and the confirmation says how many.
 */
export default function useAddAllColleagues(params: Readonly<AddAllColleaguesParams>): AddAllColleaguesAction {
  const { alreadyOnTeamUserIds, teamRestricted, onColleaguesChosen } = params;
  const confirm = useConfirmDialog();
  const [fetchColleaguePage] = useLazyGetUserCoworkersQuery();
  const [isWorking, setIsWorking] = React.useState(false);

  async function handleAddAllColleagues() {
    setIsWorking(true);
    const outcome = await gatherColleagues();
    setIsWorking(false);

    if (!outcome) {
      enqueueSnackbar("Could not load your colleagues", { variant: "error" });
      return;
    }
    if (outcome.additions.length === 0 && outcome.leftOutForCredentials > 0) {
      enqueueSnackbar(nobodyAddedForCredentialsMessage(outcome.leftOutForCredentials), {
        variant: "warning",
        preventDuplicate: true,
      });
      return;
    }
    if (outcome.additions.length === 0) {
      enqueueSnackbar("Every colleague is already on this team", {
        variant: "info",
        preventDuplicate: true,
      });
      return;
    }

    const prompt = addAllColleaguesPrompt({
      additionCount: outcome.additions.length,
      teamRestricted,
      leftOutForCredentialsCount: outcome.leftOutForCredentials,
    });
    const response = await confirm({ ...prompt, cancelButtonText: "Cancel" });
    if (response === ConfirmResponse.Cancel) return;

    onColleaguesChosen(outcome.additions);
    if (outcome.truncated) {
      enqueueSnackbar("Your firm has more colleagues than this could add at once. Add the rest by name.", {
        variant: "warning",
      });
    }
  }

  async function gatherColleagues() {
    try {
      const roster = await fetchAllColleagues(async (pagination) => {
        const page = await fetchColleaguePage({
          query: new UserCoworkerQuery(
            new UserCoworkerFilterParameters(),
            new UserCoworkerOrderParameters(UserCoworkerOrderField.Name, OrderDirection.Ascending),
            pagination,
          ),
          append: false,
        }).unwrap();
        return page;
      });
      return {
        additions: colleaguesToAdd(roster.colleagues, alreadyOnTeamUserIds),
        leftOutForCredentials: colleaguesLeftOutForCredentials(roster.colleagues, alreadyOnTeamUserIds),
        truncated: roster.truncated,
      };
    } catch (error) {
      console.error("Error loading colleagues to add to the vendor team", error);
      return undefined;
    }
  }

  return {
    addAllColleagues: () => void handleAddAllColleagues(),
    isWorking,
  };
}
